import React, { FC, useCallback, useContext, useMemo } from 'react';

import { useFormikContext } from 'formik';

import BasicButton from 'components/buttons/basicButton';
import { SearchableSelectType } from 'components/inputs/searchableSelect';
import AampeCreateSelectDropdown from 'components/shared/AampeCreateSelectDropdown/AampeCreateSelectDropdown';
// Constants
import styled from 'styled-components';

import aampeTheme from '../../../../../constants/aampeTheme';

// Models
import {
    EditorContext,
    MessageContentContext,
} from '../../../../../context/EditorContext';

import useGetFormulaMessageContents from 'hooks/formulas/useGetFormulaMessageContent';
import useHandleQueryParams from 'hooks/helper/useHandleQueryParams';
import useLanguagesQuery from 'hooks/languages/useLanguagesQuery';

import {
    ElementKind,
    ImageVariantElement,
    SectionElement,
} from '../../../../../models/editor.model';
import { MessageContent } from '../../../../../models/message-content.model';
import { FormulaTestMessageFormValues } from 'models/formula.model';

// Components
import TestFormulaElementSelectorBody from './TestFormulaElementSelectorBody';
import TestFormulaRandomizeButton from './TestFormulaRandomizeButton';
import { initialFilters } from './constant';
import TestFormulaDatasetBlock from './elements/TestFormulaDatasetBlock';

interface TestFormulaSelectorProps {}

const TestFormulaElementSelector: FC<TestFormulaSelectorProps> = () => {
    const { values, setFieldValue } =
        useFormikContext<FormulaTestMessageFormValues>();
    const [, setQueryParams] = useHandleQueryParams();

    const { editorDetails, setEditorDetails } = useContext(EditorContext);
    const { isContentUpdating } = useContext(MessageContentContext);

    const { data: messageContents } = useGetFormulaMessageContents(
        +editorDetails?.formula?.id!,
    );

    const selectedMessageContent = useMemo<MessageContent | undefined>(() => {
        if (values.languageId) {
            return messageContents?.find(
                (mc) => mc.language_id === values.languageId,
            );
        }
    }, [values, messageContents]);

    const structure = useMemo<(SectionElement | ImageVariantElement)[]>(
        () => selectedMessageContent?.structure as SectionElement[],
        [selectedMessageContent],
    );

    const { data: allLanguages } = useLanguagesQuery();

    const languages = useMemo<string[] | undefined>(() => {
        if (messageContents) {
            return messageContents?.map((mc) => mc.language_id);
        }
    }, [messageContents]);

    const searchableSelectLanguages = useMemo<SearchableSelectType[]>(() => {
        const _lang: SearchableSelectType[] = [];
        allLanguages?.forEach((lang) => {
            if (languages?.includes(lang.id))
                _lang.push({ value: lang.id, label: lang.name });
        });

        return _lang;
    }, [languages, allLanguages]);

    const setSelectedLanguage = useCallback(
        (languageId: string) => {
            if (isContentUpdating) {
                return;
            }
            const newMessageContent = messageContents?.find(
                (mc) => mc.language_id === languageId,
            );
            setFieldValue('languageId', languageId);
            setFieldValue('message_content_id', newMessageContent?.id);
            setFieldValue('filters', initialFilters);
            setQueryParams({ messageContentId: newMessageContent?.id });
        },
        [isContentUpdating, setFieldValue, setQueryParams, messageContents],
    );
    const showDataset = useMemo(() => {
        if (!structure) return false;
        if (selectedMessageContent?.deeplink_dataset_field_id) {
            return true;
        }
        return structure.some(
            (fs) =>
                ('kind' in fs &&
                    fs.kind === ElementKind.image &&
                    fs.datasetField) ||
                fs.children.some(
                    (child) =>
                        'kind' in child && child.kind === ElementKind.dataset,
                ),
        );
    }, [structure, selectedMessageContent]);

    const clearFilters = () => {
        setFieldValue('filters', initialFilters);
        setFieldValue('componentAttachedLabel', {});
        setEditorDetails({
            ...editorDetails,
            selectRandomFilters: false,
        });
    };

    return (
        <Container>
            <Header>
                {languages && (
                    <LanguageBlock>
                        Language:
                        <AampeCreateSelectDropdown
                            name={'Language dropdown'}
                            options={searchableSelectLanguages}
                            value={
                                values?.languageId
                                    ? searchableSelectLanguages.find(
                                          (lang) =>
                                              lang.value === values.languageId,
                                      )
                                    : undefined
                            }
                            onChange={(_, value) => {
                                if (!value || Array.isArray(value)) return;
                                setSelectedLanguage(String(value.value));
                            }}
                            isClearable={false}
                            creatable={false}
                            customStyle={{
                                control: (base, state) => ({
                                    ...base,
                                    border: 'none',
                                    boxShadow: 'none',
                                    backgroundColor: state.isDisabled
                                        ? 'transparent'
                                        : base.backgroundColor,
                                }),
                                singleValue: (base, state) => ({
                                    ...base,
                                    color: state.isDisabled
                                        ? aampeTheme.colors.neutral09
                                        : base.backgroundColor,
                                }),
                            }}
                            isDisabled={languages?.length === 1}
                            components={
                                languages?.length === 1
                                    ? {
                                          DropdownIndicator: () => null,
                                      }
                                    : undefined
                            }
                        />
                    </LanguageBlock>
                )}
                <TestFormulaRandomizeButton />
                <StyledBasicButton
                    name={'Clear'}
                    onClick={clearFilters}
                    type={'button'}
                />
            </Header>
            {showDataset && <TestFormulaDatasetBlock />}
            {structure.map((section, idx) => (
                <Section key={idx}>
                    <SectionHeader>
                        {section.sectionType ?? section.kind}
                    </SectionHeader>
                    <TestFormulaElementSelectorBody formulaSection={section} />
                </Section>
            ))}
        </Container>
    );
};

export default TestFormulaElementSelector;

const Container = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(6),
});

const Header = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
});

const LanguageBlock = styled.div({
    display: 'flex',
    gap: aampeTheme.spacing4(1),
    alignItems: 'center',
    color: aampeTheme.colors.neutral09,
});

const StyledBasicButton = styled(BasicButton)({
    backgroundColor: 'transparent',
    color: aampeTheme.colors.solidA01,
});

const Section = styled.div({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: aampeTheme.spacing4(1),
});

const SectionHeader = styled.h3({
    display: 'block',
    width: '100%',
    fontSize: aampeTheme.size.font.title,
    lineHeight: aampeTheme.size.lineHeight.default,
    fontWeight: aampeTheme.weights.medium,
    textTransform: 'capitalize',
    margin: aampeTheme.spacing4(0, 0, 2, 0),
});
